import type { ReactNode } from "react";
import { cx } from "@app/lib/ui-foundation";
import "./invitation-layout.css";

/**
 * A detail section is the repeated "heading + body" block used for the
 * Description, Venue and Event Dates areas. The heading slot lets each caller
 * decide whether to render a plain `<h2>` (landing/view) or a heading row with
 * an edit/save control (edit mode).
 */
export type InvitationDetailSection = {
  heading: ReactNode;
  body: ReactNode;
};

export type InvitationLayoutProps = {
  /** Adds the `events-invitation--editable` modifier used for edit affordances. */
  editable?: boolean;
  /** Optional page section title above the hero (landing: "SELECT YOUR CURRENCY"). */
  sectionTitle?: ReactNode;
  heroImageSrc?: string;
  heroLabel: string;
  /** Hero title slot: a plain `<h1>` (view) or an inline-editable control (edit). */
  heroTitle: ReactNode;
  /** Tagline slot. */
  heroTagline: ReactNode;
  /** Optional artwork displayed between the hero and event details. */
  heroFooter?: ReactNode;
  /**
   * Content rendered after the tagline inside the hero card. Edit mode passes a
   * divider + editable city; landing passes the currency selector + GO button.
   */
  heroAfterTagline?: ReactNode;
  /** Details-section title slot (edit: "Create Your Personal Invitation"). */
  detailsTitle?: ReactNode;
  description: InvitationDetailSection;
  venue: InvitationDetailSection;
  dates: InvitationDetailSection;
  /** Footer rendered after the main element (landing only). */
  footer?: ReactNode;
  /** Actions rendered at the end of the details section (edit Save controls). */
  actions?: ReactNode;
};

/**
 * Shared, purely presentational layout for the event invitation. It contains no
 * data fetching, auth, or mutation logic so it is safe to reuse on both the
 * public landing route and the protected edit route. Edit affordances (pencil
 * buttons, inputs, Save) and landing-only elements (currency selector, footer)
 * are supplied by callers via slots.
 */
export function InvitationLayout({
  editable = false,
  sectionTitle,
  heroImageSrc,
  heroLabel,
  heroTitle,
  heroTagline,
  heroFooter,
  heroAfterTagline,
  detailsTitle,
  description,
  venue,
  dates,
  footer,
  actions,
}: InvitationLayoutProps) {
  return (
    <>
      <main
        className={cx(
          "events-invitation",
          editable && "events-invitation--editable",
        )}
      >
        {sectionTitle ? (
          <h2 className="events-invitation__section-title">{sectionTitle}</h2>
        ) : null}

        <section className="events-invitation-hero">
          {heroImageSrc ? (
            <img
              className="events-invitation-hero__image"
              src={heroImageSrc}
              alt=""
              aria-hidden="true"
            />
          ) : null}
          <div className="events-invitation-hero__card">
            <p className="events-invitation-hero__label">{heroLabel}</p>

            {heroTitle}

            {heroTagline}

            {heroAfterTagline}
          </div>
        </section>

        {heroFooter}

        <section className="events-invitation-details">
          {detailsTitle}

          <div className="events-invitation-details__section">
            {description.heading}
            {description.body}
          </div>

          <div className="events-invitation-details__section">
            {venue.heading}
            {venue.body}
          </div>

          <div className="events-invitation-details__section">
            {dates.heading}
            {dates.body}
          </div>

          {actions}
        </section>
      </main>

      {footer}
    </>
  );
}
